<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        :active-merge="activeMerge"
        class="product-form__options"
        :is-gift-card="isGiftCard"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        :source="source"
        :is-shipping-unavailable="isShippingUnavailable && blockedProductsEnabled"
        @form-submitted="handleDynamicYieldOverlay"
        @update-dickey="handleIsDickey"
        @update-merge="handleUpdateMerge"
        @update-product-handle="handleUpdateProductHandle"
        @update-variant="handleUpdateVariant"
      />

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <div class="product-form__size-features">

        <div
          v-if="hasSizeOption && !isMobile"
          class="product-form__size-guide-wrapper"
        >
          <btn
            class="product-options__size-guide-cta"
            modifiers="text inline"
            type="button"
            class-label="text-body-2xs-regular"
            :label="$string('product.form.size_guide')"
            @click="openSizeGuide"
          />
        </div>
      </div>

      <recipient-form
        v-if="$variable('settings.product.enableGiftingFieldsOnPdp') && isGiftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />

      <klaviyo-bis-form
        v-if="backInStockEnabled"
        :product="product"
        :variant="variant"
        @form-submitted="handleDynamicYieldOverlay"
      />

      <div
        v-else
        class="product-form__footer"
      >
        <div
          v-if="isShippingUnavailable && blockedProductsEnabled"
          class="product-form__shipping-unavailable"
          v-text="$string('product.form.shipping_unavailable')"
        />
        <template v-else>
          <btn
            class="product-form__add-to-cart"
            :label="addToCartText"
            :disabled="addToCartDisabled || isFreeGift"
            :show-state-icon="state === 'adding'"
            type="submit"
          >
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>

          <p
            v-if="preorderMessage"
            class="product-form__preorder text-body-2xs-regular text-center"
            v-html="preorderMessage"
          />
        </template>
      </div>

      <div
        v-if="section.enableShopPay"
        class="product-form__shop-pay-wrapper">
        <p
          class="product-form__shop-pay-message text-body-2xs-regular"
          v-text="$string('product.form.shop_pay_message')"
        />

        <responsive-image
          class="product-form__shop-pay-icon"
          :image="shopPayImage"
          :loaded-animation="false"
          :lazyload="false"
          sizes="58px"
          :padding="false"
          ratio="3:1"
        />
      </div>
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Object} activeMerge - Active merge object.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean} isGiftCard - If this is a Shopify or Clutch gift card.
 * @param {Boolean} isShippingUnavailable - If shipping is unavailable.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits addToCart - Add to cart.
 * @emits updateMerge - Update merge object.
 * @emits updateProductHandle - Update current product handle.
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapState } from 'vuex'

import KlaviyoBisForm from '~async/klaviyo-bis/klaviyo-bis-form'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductOptions from '~async/product-form/product-options'
import RecipientForm from '~async/product-form/recipient-form'

import IconLoading from '~icons/general/loading.svg'

import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    KlaviyoBisForm,
    NotificationPanel,
    ProductOptions,
    RecipientForm,
    ResponsiveImage,
  },

  props: {
    activeMerge: {
      type: Object,
      default: () => ({}),
    },
    isFreeGift: {
      type: Boolean,
      default: false,
    },
    isShippingUnavailable: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        displayRecipientForm: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        dynamicYieldTarget: '',
        excludedInitialSelectOptions: ['size', 'taille', 'denominations'],
        hideGiftCardOptionNames: true,
        id: '',
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
        enableShopPay: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'add-to-cart': () => true,
    'update-merge': (payload) => typeof payload === 'object',
    'update-product-handle': (payload) => typeof payload === 'string',
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      customReportProps: {},
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          recipientMessage: '',
          'Recipient Email': '',
          'Recipient\'s first name': '',
          'Last name of recipient': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      quantity: 1,
      showBackInStockForm: false,
      state: 'ready',
      timeout: {},
      isDickey: false,
      shopPayImage: { src: 'https://cdn.shopify.com/s/files/1/0089/4432/0612/files/image-20250513-145014.png' },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      isMobile: (state) => state.index.screen.mobile,
      stateProduct: (state) => state.quickAddToCart.product,
    }),

    /**
     * Compute whether to siza variant is present
     * @returns {Boolean}
     */
    hasSizeOption() {
      return this.product.options?.some(option => option.name === 'Size' || option.name === 'Taille') &&
        this.product.variants?.length > 1
    },

    /**
     * Compute whether to disable add to cart button.
     * @returns {Boolean}
     */
    addToCartDisabled() {
      return !this.variant ||
        (this.variant && !this.variant.available) ||
        this.state === 'adding' ||
        this.state === 'added'
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.variant === false && this.state === 'ready') {
        if (
          (this.product && Array.isArray(this.product.variants)) &&
          this.product.preorder.skus !== null) {
          const soldOutCount = this.product.variants.filter(variant => !variant.available).length
          const preorderCount = this.product.preorder.skus.length
          if ((soldOutCount + preorderCount) === this.product.variants.length) {
            return this.$string('product.cta.preorder')
          }
        }
      }
      if (this.variant && !this.variant.available) {
        return this.$string('product.cta.sold_out')
      }

      if ((!this.variant && this.preorderVariant) || (!this.variant && this.preorderVariants)) {
        return this.$string('product.cta.sold_out')
      }

      if (this.preorderVariant || this.preorderVariants) {
        return this.$string('product.cta.preorder')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute whether to show back in stock button.
     * @returns {Boolean}
     */
    backInStockEnabled() {
      return this.variant && !this.variant.available
    },

    /**
     * Compute whether blocked products are enabled.
     * @returns {Boolean}
     */
    blockedProductsEnabled() {
      return window.cnvs?.settings?.blockedProduct || false
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.isGiftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null
      }

      const filteredFields = {}

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return
        }

        filteredFields[key] = value
      })

      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      }
    },

    /**
     * Compute if the user has location set to USA.
     * @returns {Boolean}
     */
    isUnitedStates() {
      return this.current?.country?.code.toUpperCase() === 'US'
    },

    /**
     * Compute preorder line item properties.
     * @returns {Null|Object}
     */
    preorderLineItemProperties() {
      if (!this.preorderVariant) {
        return null
      }

      const date = this.$formatPreorderDate({
        date: this.product?.preorder?.date,
        dateFormat: 'preorder_cart_html',
      })

      const preorderBadge = this.$string('product.preorder.cart', { replace: { date } })

      return {
        preorderBadge,
      }
    },

    /**
     * Compute preorder message.
     * - If there should be one.
     * @returns {String}
     */
    preorderMessage() {
      if (!this.product?.preorder?.date || !this.product?.preorder?.skus?.length) {
        return ''
      }

      if (this.preorderVariant || this.preorderVariants) {
        const date = this.$formatPreorderDate({
          date: this.product?.preorder?.date,
          dateFormat: 'preorder_pdp_html',
        })

        return this.$string('product.preorder.form', { replace: { date } })
      }

      return ''
    },

    /**
     * Compute if selected variant is a preorder.
     * @returns {Boolean}
     */
    preorderVariant() {
      return this.product?.preorder?.skus?.includes(this.variant?.sku)
    },

    /**
     * Compute if all variants are preorders.
     * @returns {Boolean}
     */
    preorderVariants() {
      return this.product?.variants?.every((variant) => {
        return this.product?.preorder?.skus?.includes(variant.sku)
      })
    },

    /**
     * Compute show product form element.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.loading && !this.product) {
        return true
      }

      const multipleOptions = this.product?.options?.some((option) => {
        return option.values.length > 1 ||
          this.$variable('settings.product.productSwatchOptionNames')?.includes(
            option.name?.toLowerCase(),
          )
      })

      /**
       * If multiple variants then ensure they have more than one option.
       */
      const showOptionsMultiVariant =
        !this.product.hasOnlyDefaultVariant &&
        multipleOptions

      /**
       * If single variant then check if section settings enable display.
       */
      const showOptionsSingleVariant =
        this.section.displaySingleValueOptions &&
        this.section.displaySingleVariantOption

      return showOptionsMultiVariant || showOptionsSingleVariant
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
      this.showBackInStockForm = false
    },

    /**
     * Watch state product to reset state.
     */
    stateProduct(newStateProduct) {
      if (!newStateProduct) {
        this.resetState()
      }
    },

    state() {
      if (this.isMobile && this.state === 'added') {
        setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))
      }
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
    this.customReportProps = this.getCustomReportProps()

  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      setProduct: 'quickAddToCart/setQuickAddProduct',
      setSize: 'quickAddToCart/setSize',
      setShowUpsell: 'productFormDrawer/setShowOnSuccess',
      setSizeGuideProduct: 'sizeGuideDrawer/setSizeGuideProduct',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.$string('product.form.select_variant')
        return
      }

      this.lineItemProperties = {}
      this.addMetafieldsToProperties()

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }
      this.lineItemProperties = {
        ...this.lineItemProperties,
        ...this.customReportProps,
      }

      const items = [
        {
          id: this.variant.id,
          properties: {
            ...this.giftCardLineItemProperties,
            ...this.lineItemProperties,
          },
          quantity: this.quantity,
        },
      ]

      this.setShowUpsell(true)

      try {
        await this.addItem(items)
        this.state = 'added'

        this.$emit('add-to-cart')
        if (this.section.enableFitPredictor) {
          this.trackSspAddToCart()
        }
        this.displaySuccessModal()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Tracking adding to cart for SSP
     */
    trackSspAddToCart() {
      if (!window.ssp || !this.product || !this.variant) {
        return
      }

      window.ssp('track', 'addVariantToCart', {
        productId: `${this.product.id}`,
        variantId: `${this.variant.id}`,
        quantity: this.quantity || 1,
      })
    },

    /**
     * Set product and display success modal.
     */
    displaySuccessModal() {
      this.setProduct(this.formatSuccessMessageProduct())

      const size = this.variant.selectedOptions?.find((option) => {
        return option.name?.toLowerCase() === 'size' || option.name?.toLowerCase() === 'taille'
      })?.value

      if (!this.isGiftCard && size) {
        this.setSize(size)
      }

      const overlayNamespace = this.isMobile
        ? 'productFormSuccessMobile'
        : 'quick-add-success-modal'

      this.openOverlay({
        ignoreDismissed: true,
        namespace: overlayNamespace,
      })
    },

    /**
     * Format product for ATC success modal by updating featured image to
     * flat image of currently selected color, if exists.
     * @returns {Object}
     */
    formatSuccessMessageProduct() {
      const flatImageIndex = this.product?.metafields?.flatImageIndex
      const images = this.product?.images || this.product?.media

      const filteredImages = images.filter((image) => {
        return image?.alt?.includes(' - ')
      })

      const imageOverride = filteredImages[flatImageIndex] || images[flatImageIndex]

      if (imageOverride) {
        return {
          ...this.product,
          featuredImage: this.variant?.image || imageOverride?.image || imageOverride,
        }
      }

      return this.product
    },

    /*
     * Add metafields to properties to retrieve them in cart.
     */
    addMetafieldsToProperties() {
      const metafields = (this.product || this.liquid).metafields

      this.lineItemProperties = {
        ...this.lineItemProperties,
        metafields: {},
      }

      if (metafields.bottomBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          bottomBadge: metafields.bottomBadge,
        }
      }

      if (metafields.discountCode) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          discountCode: metafields.discountCode,
        }
      }

      if (metafields.topBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          topBadge: metafields.topBadge,
        }
      }

      if (metafields.specialtyBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          specialtyBadge: metafields.specialtyBadge,
        }
      }

      if (metafields.matchingSet &&
        (metafields.matchingSet.product !== null ||
          metafields.matchingSet.description !== null ||
          metafields.matchingSet.button !== null)
      ) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          matchingSet: metafields.matchingSet,
        }
      }

      /**
       * Add preorder date.
       */
      if (!this.preorderLineItemProperties) {
        return
      }

      this.lineItemProperties.metafields = {
        ...this.lineItemProperties.metafields,
      }

      const date = this.$formatPreorderDate({
        date: this.product?.preorder?.date,
        dateFormat: 'preorder_checkout',
      })

      const preorderDate = this.$string('product.preorder.form', { replace: { date } })

      this.lineItemProperties = {
        ...this.lineItemProperties,
        Preorder: preorderDate,
      }
    },

    /**
     * Reset product form state.
     */
    resetState() {
      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.isGiftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            recipientMessage: '',
            'Recipient Email': '',
            'Recipient\'s first name': '',
            'Last name of recipient': '',
            'Send on': '',
          },
        }
      }

      /**
       * Reset variant selection based on setting.
       */
      if (
        this.section.resetFormBehaviour !== 'clear' ||
        this.product?.variants?.length < 2
      ) {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle merge update.
     * @param {Object} group - Updated object
     */
    handleUpdateMerge(object) {
      this.$emit('update-merge', object)
    },

    /**
     * Handle update variant event.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    async handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)

      await this.$nextTick()

      if (this.variant?.sku || this.product?.selectedOrFirstAvailableVariant?.sku) {
        this.customReportProps = this.getCustomReportProps()
      }
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Handle dynamic yield recommendations.
     */
    handleDynamicYieldOverlay() {
      cnvs.EventBus.emit('dy:bis')
    },

    /**
     * Check if Dickey + Jacket set
     */
    handleIsDickey(isDickey) {
      this.isDickey = isDickey
    },

    /**
     * Properties for custom reporting
     */
    getCustomReportProps() {
      const trackingHistory = JSON.parse(localStorage.getItem('trackingHistory'))
      const pageSource = trackingHistory?.pageSource
      const sku = this.variant?.sku || this.product?.selectedOrFirstAvailableVariant?.sku
      const preorderVariant = this.product?.preorder?.skus?.includes(sku)
      const pageUrl = window.location.pathname
      const metaobjectName = trackingHistory?.metaobjectName ||
        `${this.product?.title} ${this.product?.metafields?.swatches?.[0]?.name}`
      let customReportObject = {}

      // eslint-disable-next-line no-underscore-dangle
      customReportObject._PDP_ATC_Path = window.location.pathname

      if (pageSource === 'plpHotspot' ||
        pageSource === 'sectionHotspot' ||
        pageSource === 'pdpShopTheLook') {
        customReportObject[`_${pageSource}`] = `${sku}_${pageUrl}_${metaobjectName}`
      } else if (pageSource === 'productTray') {
        customReportObject[`_${pageSource}`] = `${sku}_${pageUrl}`
      } else if (pageSource === 'addedFromWishlist') {
        customReportObject[`_${pageSource}`] = sku
      }

      if (preorderVariant || this.preorderVariants) {
        const product = this.product || this.stateProduct
        const date = this.$formatPreorderDate({
          date: product?.preorder?.date,
          dateFormat: 'preorder_cart_html',
        })

        customReportObject = {
          ...customReportObject,
          _preOrderBadge: `Preorder: Ships after ${date}`,
          _preOrderDate: product?.preorder?.date,
        }
      }

      if (this.product?.tags?.includes('finalsale') ||
        this.product.tags?.includes('Final Sale')) {
        customReportObject = {
          ...customReportObject,
          _finalSale: `finalSale: Product is final sale`,
        }
      }

      if (this.isDickey) {
        customReportObject = {
          ...customReportObject,
          _dickeyJacketSet: sku,
        }
      }

      return customReportObject
    },

    /**
     * Handle update product handle.
     * @param {Object} handle - Product handle to update.
     */
    handleUpdateProductHandle(handle) {
      this.$emit('update-product-handle', handle)
    },

    /**
     * Open size guide drawer.
     */
    openSizeGuide() {
      this.setSizeGuideProduct(this.product)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeGuideDrawer',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
